/**
 * @file App.jsx
 * @description Root application component.
 * Defines the route structure, wraps authenticated pages with ProtectedRoute,
 * and applies role guards where needed.
 *
 * Provider hierarchy (outermost → innermost):
 *  ToastProvider  — site-wide toast notifications (renders ToastStack internally)
 *  SocketProvider — site-wide Socket.IO connection (renders ConnectionLostModal internally)
 *
 * Route layout:
 *  /               → Dashboard (all authenticated roles)
 *  /tokens         → Token management (admin, operator)
 *  /endpoints      → Endpoint config (admin, operator)
 *  /logs           → Log viewer (all roles)
 *  /users          → User management (admin only)
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ToastProvider } from './context/ToastContext';
import { SocketProvider } from './context/SocketContext';

import Navbar         from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard      from './components/RoleGuard';

import LoginPage    from './pages/LoginPage';
import Setup        from './pages/Setup';
import Dashboard   from './pages/Dashboard';
import Tokens      from './pages/Tokens';
import Endpoints   from './pages/Endpoints';
import ProxyRoutes from './pages/ProxyRoutes';
import Logs        from './pages/Logs';
import Users       from './pages/Users';
import Settings    from './pages/Settings';

/**
 * App component — single <Routes> block to prevent catch-all redirect loops.
 *
 * Two <Routes> blocks that both match the current URL cause a loop:
 *  1. The catch-all in block #2 redirects /login → /
 *  2. ProtectedRoute in block #2 redirects / → /login
 *  3. Repeat until the browser throws a history.replaceState() rate-limit error.
 *
 * Solution: one Routes block for all routes. The /login padding difference is
 * handled with useLocation — no padding on the login page, navbar-height elsewhere.
 */
export default function App() {
  const { pathname } = useLocation();
  const isLoginPage  = pathname === '/login' || pathname === '/setup';

  return (
    <ToastProvider>
      <SocketProvider>
        {/* Navbar returns null on /login via its own location check */}
        <Navbar />

        {/*
          A single top-level padding wrapper.
          Login page gets no top padding (it fills the full viewport).
          All other pages are offset by the fixed navbar height.
        */}
        <main style={{ paddingTop: isLoginPage ? 0 : 'var(--navbar-height)' }}>
          <Routes>
            {/* Public — no auth required */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<Setup />} />

            {/* Dashboard — all authenticated roles */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Token management — admin and operator only */}
            <Route
              path="/tokens"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'operator']}>
                    <Tokens />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Endpoint config — admin and operator only */}
            <Route
              path="/endpoints"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'operator']}>
                    <Endpoints />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Proxy route management — admin and operator only */}
            <Route
              path="/proxy-routes"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'operator']}>
                    <ProxyRoutes />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Log viewer — all authenticated roles */}
            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <Logs />
                </ProtectedRoute>
              }
            />

            {/* User management — admin only */}
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <Users />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Global settings — admin only */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <Settings />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Catch-all: unknown paths → dashboard (ProtectedRoute handles auth) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </SocketProvider>
    </ToastProvider>
  );
}
